import { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { formatCnpj, formatCep, validateCnpj } from '../../utils/masks';

const EMPTY = {
  nome: '', nome_fantasia: '', cnpj: '', razao_social: '',
  segmento: '', porte: '',
  endereco_rua: '', endereco_numero: '', endereco_bairro: '',
  cidade: '', estado: '', endereco_cep: '',
  telefone_empresa: '', site: '', email: '', obs: '', esn_id: '',
  parent_conta_id: '',
};

// ZYN-311 — estado e lógica do ContaFormModal extraídos para hook (mesmo
// padrão de client/src/components/lead-form/useLeadForm.js).
export function useContaForm({ conta, onSaved, initialTab = 'dados' }) {
  const [form, setForm] = useState(EMPTY);
  const [showChildModal, setShowChildModal] = useState(false);
  const [childPrefills, setChildPrefills] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const [esns, setEsns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjMsg, setCnpjMsg] = useState('');
  const [error, setError] = useState('');
  // ZYN-306: conta já cadastrada com este CNPJ (checagem proativa + fallback do 409)
  const [existingConta, setExistingConta] = useState(null);

  // Conta Vinculada (linked leads)
  const [linkedLeads, setLinkedLeads] = useState([]);
  const [leadSearch, setLeadSearch] = useState('');
  const [leadResults, setLeadResults] = useState([]);
  const [leadSearchLoading, setLeadSearchLoading] = useState(false);
  const debounceRef = useRef(null);

  // Candidatos a matriz: contas com mesmo CNPJ raiz (8 primeiros dígitos)
  const [parentCandidates, setParentCandidates] = useState([]);

  // Busca livre de matriz (grupo econômico / franquia — CNPJ raiz diferente).
  // parentSelected guarda o objeto conta para render do rótulo; query/results
  // são o dropdown de busca.
  const [parentSelected, setParentSelected] = useState(null);
  const [parentQuery, setParentQuery] = useState('');
  const [parentResults, setParentResults] = useState([]);
  const [parentOpen, setParentOpen] = useState(false);
  const parentDebounceRef = useRef(null);
  const parentBoxRef = useRef(null);

  useEffect(() => {
    // Remap: esns_view usa users.id como `id`, mas contas.esn_id guarda o
    // legacy esns.id (= users.esn_id). Usa legacy_esn_id para o matching
    // do <select> funcionar em leituras e o save persistir o ID correto.
    // getEsns() chama /api/team — payload expõe `esn_id` (legacy), não
    // `legacy_esn_id`. Aceitamos ambos para cobrir /team e /esns.
    api.getEsns()
      .then(list => setEsns(
        list.map(e => ({ ...e, id: e.legacy_esn_id ?? e.esn_id ?? e.id }))
      ))
      .catch(() => {});
    if (conta) {
      setForm({
        nome: conta.nome || '',
        nome_fantasia: conta.nome_fantasia || '',
        cnpj: formatCnpj(conta.cnpj || ''),
        razao_social: conta.razao_social || '',
        segmento: conta.segmento || '',
        porte: conta.porte || '',
        endereco_rua: conta.endereco_rua || '',
        endereco_numero: conta.endereco_numero || '',
        endereco_bairro: conta.endereco_bairro || '',
        cidade: conta.cidade || '',
        estado: conta.estado || '',
        endereco_cep: conta.endereco_cep || '',
        telefone_empresa: conta.telefone_empresa || '',
        site: conta.site || '',
        email: conta.email || '',
        obs: conta.obs || '',
        esn_id: conta.esn_id || '',
        parent_conta_id: conta.parent_conta_id || '',
      });
      // Load leads linked to this conta
      api.getLeads({ conta_id: conta.id, limit: 50 })
        .then(res => setLinkedLeads(Array.isArray(res) ? res : (res.leads || [])))
        .catch(() => {});
    }
  }, [conta]);

  // Sugestões automáticas: contas com mesmo CNPJ raiz (candidato a filial legal)
  useEffect(() => {
    const digits = form.cnpj.replace(/\D/g, '');
    if (digits.length < 8) { setParentCandidates([]); return; }
    const raiz = digits.slice(0, 8);
    api.getContas({ search: raiz, limit: 50 })
      .then(res => {
        const rows = res?.data || res || [];
        setParentCandidates(rows.filter(c => c.id !== conta?.id));
      })
      .catch(() => setParentCandidates([]));
  }, [form.cnpj, conta?.id]);

  // Quando edita uma conta que já tem matriz vinculada, busca o objeto da
  // matriz para mostrar o nome no combobox (o backend só devolve o id).
  useEffect(() => {
    if (!form.parent_conta_id) { setParentSelected(null); return; }
    // Se já temos um match local nos candidatos, usa direto
    const local = parentCandidates.find(c => c.id === Number(form.parent_conta_id));
    if (local) { setParentSelected(local); return; }
    // Se já é o mesmo parentSelected, nada a fazer
    if (parentSelected?.id === Number(form.parent_conta_id)) return;
    api.getConta(form.parent_conta_id)
      .then(c => c && setParentSelected(c))
      .catch(() => {});
  }, [form.parent_conta_id, parentCandidates]);

  // Busca livre (debounced) — serve para franquia / grupo econômico com CNPJ
  // raiz diferente. Só dispara com 2+ caracteres para evitar requests inúteis.
  useEffect(() => {
    if (parentDebounceRef.current) clearTimeout(parentDebounceRef.current);
    const q = parentQuery.trim();
    if (q.length < 2) { setParentResults([]); return; }
    parentDebounceRef.current = setTimeout(() => {
      api.getContas({ search: q, limit: 15 })
        .then(res => {
          const rows = res?.data || res || [];
          setParentResults(rows.filter(c => c.id !== conta?.id));
        })
        .catch(() => setParentResults([]));
    }, 350);
    return () => clearTimeout(parentDebounceRef.current);
  }, [parentQuery, conta?.id]);

  // Click-outside fecha o dropdown
  useEffect(() => {
    function handler(e) {
      if (parentBoxRef.current && !parentBoxRef.current.contains(e.target)) {
        setParentOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  async function fetchCep(cepRaw) {
    const digits = cepRaw.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm(f => ({
          ...f,
          endereco_rua: data.logradouro || f.endereco_rua,
          endereco_bairro: data.bairro || f.endereco_bairro,
          cidade: data.localidade || f.cidade,
          estado: data.uf || f.estado,
        }));
      }
    } catch {
      // silently ignore
    } finally {
      setCepLoading(false);
    }
  }

  async function fetchCnpj(cnpjOverride) {
    // cnpjOverride: usado pelo auto-lookup do set('cnpj'), onde o state ainda
    // não reflete o valor recém-digitado (ZYN-305)
    const digits = (typeof cnpjOverride === 'string' ? cnpjOverride : form.cnpj).replace(/\D/g, '');
    if (digits.length !== 14) { setCnpjMsg('CNPJ incompleto'); return; }
    setCnpjLoading(true);
    setCnpjMsg('');
    try {
      const data = await api.lookupCnpj(digits);
      if (data.found) {
        // fill-empty-only: só preenche o que está vazio no form, pra não
        // sobrescrever edição manual do usuário
        setForm(f => ({
          ...f,
          razao_social:     f.razao_social     || data.nome          || '',
          nome_fantasia:    f.nome_fantasia    || data.nome_fantasia || '',
          cidade:           f.cidade           || data.municipio     || '',
          estado:           f.estado           || data.uf            || '',
          endereco_rua:     f.endereco_rua     || data.logradouro    || '',
          endereco_numero:  f.endereco_numero  || data.numero        || '',
          endereco_bairro:  f.endereco_bairro  || data.bairro        || '',
          endereco_cep:     f.endereco_cep     || (data.cep ? formatCep(data.cep) : ''),
          telefone_empresa: f.telefone_empresa || data.telefone      || '',
          email:            f.email            || data.email         || '',
        }));
        setCnpjMsg(`✓ ${data.nome}`);
      } else {
        setCnpjMsg('CNPJ não encontrado na Receita Federal');
      }
    } catch (e) {
      setCnpjMsg(e.message?.includes('502') ? 'Serviço de consulta indisponível — tente novamente' : 'Erro ao consultar CNPJ');
    } finally {
      setCnpjLoading(false);
    }
  }

  // ZYN-306: checa se já existe conta ativa com este CNPJ (exclui a própria em edição)
  async function checkExistingConta(digits) {
    try {
      const res = await api.getContas({ search: digits, autocomplete: '1', limit: 5 });
      const rows = res?.data || res || [];
      const match = rows.find(c =>
        (c.cnpj || '').replace(/\D/g, '') === digits && c.id !== conta?.id
      );
      setExistingConta(match || null);
    } catch {
      setExistingConta(null);
    }
  }

  function set(field, value) {
    const updated = { ...form, [field]: value };
    if (field === 'cnpj') {
      updated.cnpj = formatCnpj(value);
      setCnpjMsg('');
      const digits = updated.cnpj.replace(/\D/g, '');
      if (digits.length !== 14) setExistingConta(null);
      // ZYN-305: auto-lookup ao completar 14 dígitos (mesmo padrão do LeadForm).
      // O botão de lupa permanece como re-consulta manual.
      if (digits.length === 14 && digits !== form.cnpj.replace(/\D/g, '')) {
        fetchCnpj(updated.cnpj);
        checkExistingConta(digits);
      }
    }
    if (field === 'endereco_cep') {
      updated.endereco_cep = formatCep(value);
      if (updated.endereco_cep.replace(/\D/g, '').length === 8) fetchCep(updated.endereco_cep);
    }
    setForm(updated);
  }

  // Lead search for Conta Vinculada
  function handleLeadSearch(query) {
    setLeadSearch(query);
    clearTimeout(debounceRef.current);
    if (!query.trim()) { setLeadResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLeadSearchLoading(true);
      try {
        const res = await api.getLeads({ search: query, limit: 8 });
        const all = Array.isArray(res) ? res : (res.leads || []);
        // Exclude already linked leads
        const linkedIds = new Set(linkedLeads.map(l => l.id));
        setLeadResults(all.filter(l => !linkedIds.has(l.id)));
      } catch {
        setLeadResults([]);
      } finally {
        setLeadSearchLoading(false);
      }
    }, 400);
  }

  async function linkLead(lead) {
    if (!conta) return;
    try {
      await api.updateLead(lead.id, { conta_id: conta.id });
      setLinkedLeads(ls => [...ls, lead]);
      setLeadSearch('');
      setLeadResults([]);
    } catch (e) {
      setError('Erro ao vincular lead: ' + e.message);
    }
  }

  async function unlinkLead(lead) {
    try {
      await api.updateLead(lead.id, { conta_id: null });
      setLinkedLeads(ls => ls.filter(l => l.id !== lead.id));
    } catch (e) {
      setError('Erro ao desvincular: ' + e.message);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const cnpjDigits = form.cnpj.replace(/\D/g, '');
    if (!form.nome.trim() || !cnpjDigits) {
      setError('Nome e CNPJ são obrigatórios');
      return;
    }
    // ZYN-304: DV validado apenas quando o CNPJ mudou (contas legadas com DV
    // inválido continuam editáveis nos demais campos) — espelha o servidor.
    const originalDigits = (conta?.cnpj || '').replace(/\D/g, '');
    if (cnpjDigits !== originalDigits) {
      const v = validateCnpj(form.cnpj);
      if (!v.valid) {
        setError(v.error || 'CNPJ inválido');
        return;
      }
    }
    // ZYN-306: bloqueia criação de conta duplicada — o banner oferece abrir a existente
    if (!conta && existingConta) {
      setError(`Já existe uma conta cadastrada com este CNPJ: ${existingConta.nome}`);
      return;
    }
    setLoading(true);
    const payload = {
      ...form,
      cnpj: form.cnpj.replace(/\D/g, ''),
      endereco_cep: form.endereco_cep.replace(/\D/g, ''),
      telefone_empresa: form.telefone_empresa || null,
      esn_id: form.esn_id ? parseInt(form.esn_id) : null,
      parent_conta_id: form.parent_conta_id ? parseInt(form.parent_conta_id) : null,
    };
    try {
      if (conta) {
        await api.updateConta(conta.id, payload);
        onSaved();
      } else {
        const res = await api.createConta(payload);
        // Passa a conta recém-criada para o caller oferecer o próximo passo
        // (ex.: "Criar oportunidade" — ZYN-308)
        onSaved({ ...payload, id: res?.id });
      }
    } catch (err) {
      // ZYN-306: 409 de CNPJ duplicado ganha CTA para abrir a conta existente
      if (err.status === 409) {
        checkExistingConta(cnpjDigits);
        setError('CNPJ já cadastrado — veja a conta existente destacada abaixo do campo CNPJ.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }

  return {
    form, set, setForm,
    activeTab, setActiveTab,
    showChildModal, setShowChildModal, childPrefills, setChildPrefills,
    esns,
    loading, cepLoading, cnpjLoading, cnpjMsg,
    error,
    existingConta,
    linkedLeads, leadSearch, leadResults, leadSearchLoading, handleLeadSearch, linkLead, unlinkLead,
    parentCandidates, parentSelected, setParentSelected,
    parentQuery, setParentQuery, parentResults, parentOpen, setParentOpen, parentBoxRef,
    fetchCnpj,
    handleSubmit,
  };
}
